import React from 'react'

import { PixelCustomization } from './PixelCustomization'
import { PixelDetails } from './PixelDetails/index'
import { PixelIntegration } from './PixelIntegration'
import {
  Action,
  IntegrationData,
  PixelInfo,
  PixelInfoPayload,
} from '../../models/Pixel'
import {
  ADVANCED_SETTING_NAME,
  DEFAULT_VALUES,
} from '../../constants/constants'
import { usePixelAdvancedSettingList } from '../../hooks/usePixel'

interface CreateOrEditPixelFormProps {
  initialData?: typeof defaultData
  pixelId?: string
  isCompletionInProgress?: boolean
  type: 'create' | 'edit'
  step: number
  setStep: React.Dispatch<React.SetStateAction<number>>
  onCompletion: (data: PixelInfoPayload) => void
  pixelConversionScript: string
  onIntegrationCompletion: (integrationData: IntegrationData) => void
  isEmailBeingScheduled?: boolean
  onClose: () => void
}

const defaultData: PixelInfo = {
  name: '',
  attributionId: '1',
  customFields: [],
  financialMetrics: '',
  piggybackData: {
    url: '',
    type: 1,
  },
  conversionSetting: {
    conversionDuration: {
      click: DEFAULT_VALUES.POST_CLICK,
      view: DEFAULT_VALUES.POST_VIEW,
    },
    repeatConversion: {
      count: DEFAULT_VALUES.CONVERSION_COUNT,
      frequency: DEFAULT_VALUES.CONVERSION_FREQUENCY,
      unit: DEFAULT_VALUES.CONVERSION_UNIT,
    },
    crossModelling: true,
  },
}

const pixelInfoReducer = (state: PixelInfo, action: Action) => {
  switch (action.type) {
    case 'UPDATE_NAME':
      return {
        ...state,
        name: action.payload,
      }
    case 'UPDATE_ATTRIBUTION_ID':
      return {
        ...state,
        attributionId: action.payload,
      }
    case 'ADD_CUSTOM_FIELD':
      return {
        ...state,
        customFields: state.customFields.includes(action.payload)
          ? state.customFields
          : [...state.customFields, action.payload],
      }
    case 'REMOVE_CUSTOM_FIELD':
      return {
        ...state,
        customFields: state.customFields.filter(
          field => field !== action.payload
        ),
      }
    case 'RESET_CUSTOM_FIELD':
      return {
        ...state,
        customFields: [],
      }
    case 'UPDATE_FINANCIAL_METRICS':
      return {
        ...state,
        financialMetrics: action.payload,
      }
    case 'UPDATE_PIGGYBACK_URL':
      return {
        ...state,
        piggybackData: {
          ...state.piggybackData,
          url: action.payload,
        },
      }
    case 'UPDATE_PIGGYBACK_TYPE':
      return {
        ...state,
        piggybackData: {
          ...state.piggybackData,
          type: action.payload,
        },
      }
    case 'UPDATE_CONVERSION_DURATION':
      return {
        ...state,
        conversionSetting: {
          ...state.conversionSetting,
          conversionDuration: {
            ...state.conversionSetting.conversionDuration,
            ...action.payload,
          },
        },
      }
    case 'UPDATE_REPEAT_CONVERSION':
      return {
        ...state,
        conversionSetting: {
          ...state.conversionSetting,
          repeatConversion: {
            ...state.conversionSetting.repeatConversion,
            ...action.payload,
          },
        },
      }
    case 'TOGGLE_CROSS_MODELLING':
      return {
        ...state,
        conversionSetting: {
          ...state.conversionSetting,
          crossModelling: action.payload,
        },
      }
    default:
      return state
  }
}

export const CreateOrEditPixelForm = ({
  initialData = defaultData,
  type,
  step,
  setStep,
  onCompletion,
  pixelConversionScript,
  isCompletionInProgress,
  onIntegrationCompletion,
  isEmailBeingScheduled,
  onClose,
}: CreateOrEditPixelFormProps) => {
  const [advancedSettingEnabled, toggleAdvancedSettingVisibility] =
    React.useState(false)

  const [pixelInfoReducerState, dispatch] = React.useReducer(
    pixelInfoReducer,
    initialData
  )

  const advancedDurationSettings = usePixelAdvancedSettingList({
    enabled: type === 'create',
  })

  const initialPiggyBackDataRef = React.useRef({
    type: initialData.piggybackData.type,
    url: initialData.piggybackData.url,
  })

  const advanceConfigData = React.useMemo(() => {
    const postClickDefaultSetting = advancedDurationSettings?.data?.data.find(
      item => item.name === ADVANCED_SETTING_NAME.POST_CLICK
    )
    const postViewDefaultSetting = advancedDurationSettings?.data?.data.find(
      item => item.name === ADVANCED_SETTING_NAME.POST_VIEW
    )
    const crossModelingDefaultSetting =
      advancedDurationSettings?.data?.data?.find(
        item => item.name === ADVANCED_SETTING_NAME.CROSS_MODELING
      )
    return {
      postClickDefaultSetting,
      postViewDefaultSetting,
      crossModelingDefaultSetting,
    }
  }, [advancedDurationSettings.data?.data])

  const setDefaultAdvancedSetting = React.useCallback(() => {
    dispatch({
      type: 'UPDATE_CONVERSION_DURATION',
      payload: {
        view:
          Number(advanceConfigData.postViewDefaultSetting?.value) ??
          DEFAULT_VALUES.POST_VIEW,
        click:
          Number(advanceConfigData.postClickDefaultSetting?.value) ??
          DEFAULT_VALUES.POST_CLICK,
      },
    })

    dispatch({
      type: 'TOGGLE_CROSS_MODELLING',
      payload: advanceConfigData.crossModelingDefaultSetting?.value === 'true',
    })

    dispatch({
      type: 'UPDATE_REPEAT_CONVERSION',
      payload: {
        count: DEFAULT_VALUES.CONVERSION_COUNT,
        frequency: DEFAULT_VALUES.CONVERSION_FREQUENCY,
        unit: DEFAULT_VALUES.CONVERSION_UNIT,
      },
    })
  }, [advanceConfigData, dispatch])

  React.useEffect(() => {
    if (advancedDurationSettings.isSuccess && type === 'create') {
      setDefaultAdvancedSetting()
    }
  }, [advancedDurationSettings.isSuccess, setDefaultAdvancedSetting, type])

  const toggleAdvancedSettingWrapper = (newVal: boolean) => {
    toggleAdvancedSettingVisibility(newVal)
    if (!newVal) {
      setDefaultAdvancedSetting()
    }
  }

  const onClosePixelCustomization = () => {
    setStep(1)
    onClose()
  }

  return (
    <>
      {step === 1 && (
        <PixelDetails
          dispatch={dispatch}
          data={pixelInfoReducerState}
          type={type}
          setStep={setStep}
          onClose={onClose}
          advancedSettingEnabled={advancedSettingEnabled}
          toggleAdvancedSettingVisibility={toggleAdvancedSettingWrapper}
        />
      )}
      {step === 2 && (
        <PixelCustomization
          type={type}
          dispatch={dispatch}
          data={pixelInfoReducerState}
          setStep={setStep}
          onCompletion={onCompletion}
          isCompletionInProgress={isCompletionInProgress}
          initialPiggyBackDataRef={initialPiggyBackDataRef}
          onClose={onClosePixelCustomization}
        />
      )}
      {step === 3 && (
        <PixelIntegration
          onClose={onClose}
          pixelConversionScript={pixelConversionScript}
          onIntegrationCompletion={onIntegrationCompletion}
          isEmailBeingScheduled={isEmailBeingScheduled}
        />
      )}
    </>
  )
}
